'use client';

import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Loading from '../core/Loading';
import { Box, Typography, Card, Grid, Chip } from '@mui/material';
import { formatAddress, formatDateHourMinuteSecond } from '@/stack-shared/utils/gen-utils';
import { useGetUser } from '@/hooks/api/useUsers';
import TitleBar from '../layout/TitleBar';

const UserPage: React.FC = () => {
  const { userId } = useParams();
  const getUserMutation = useGetUser();
  const [user, setUser] = React.useState<any>(null);
  const [address, setAddress] = React.useState<any>(null);
  const [userRoleNames, setUserRoleNames] = React.useState<string[]>([]);

  useEffect(() => {
    if (userId) {
      getUserMutation.mutate({ id: userId }, {
        onSuccess: (data: any) => {
          setUser(data.user || data);
          setAddress(data.address || null);
          // Extract role names from response if available
          if (data.roles) {
            setUserRoleNames(data.roles.map((r: any) => r.name || r.label || 'Unknown Role'));
          }
        },
      });
    }
  }, [userId]);

  if (!user) {
    return <Loading loading={true} noBackdrop />;
  }

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Users', href: '/app/users' },
    { label: user.firstName && user.lastName 
      ? `${user.firstName} ${user.lastName}` 
      : user.email || userId || 'User' }
  ];

  const fullName = user.firstName && user.lastName 
    ? `${user.firstName} ${user.lastName}` 
    : user.firstName || user.lastName || user.initials || 'Unknown User';

  return (
    <>
      <TitleBar
        breadcrumbs={breadcrumbs}
        title={`User: ${fullName}`}
      />

      <Box sx={{ padding: 3 }}>
        {/* User Basic Information */}
        <Card sx={{ p: 2, marginBottom: 3 }}>
          <Typography variant="h6" gutterBottom>User Information</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <strong>Name:</strong> {fullName}
            </Grid>
            {user.initials && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Initials:</strong> {user.initials}
              </Grid>
            )}
            {user.email && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Email:</strong> {user.email}
                {user.emailConfirmed !== undefined && (
                  <Chip 
                    label={user.emailConfirmed ? 'Confirmed' : 'Not Confirmed'} 
                    color={user.emailConfirmed ? 'success' : 'warning'}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Grid>
            )}
            {user.phone && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Phone:</strong> {user.phone}
                {user.phoneConfirmedDate && (
                  <Chip 
                    label="Confirmed" 
                    color="success"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <strong>Status:</strong>
              <Chip 
                label={user.active !== false ? 'Active' : 'Inactive'} 
                color={user.active !== false ? 'success' : 'default'}
                size="small"
                sx={{ ml: 1 }}
              />
            </Grid>
            {user.language && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Language:</strong> {user.language}
              </Grid>
            )}
            {user.googleId && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Google ID:</strong> {user.googleId}
              </Grid>
            )}
          </Grid>
        </Card>

        {/* Address Information */}
        {address && (
          <Card sx={{ p: 2, marginBottom: 3 }}>
            <Typography variant="h6" gutterBottom>Address</Typography>
            <Typography variant="body1">{formatAddress(address)}</Typography>
          </Card>
        )}

        {/* Roles Information */}
        {userRoleNames.length > 0 && (
          <Card sx={{ p: 2, marginBottom: 3 }}>
            <Typography variant="h6" gutterBottom>Roles</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {userRoleNames.map((roleName, index) => (
                <Chip key={index} label={roleName} color="primary" />
              ))}
            </Box>
          </Card>
        )}

        {/* Login Information */}
        <Card sx={{ p: 2, marginBottom: 3 }}>
          <Typography variant="h6" gutterBottom>Login Information</Typography>
          <Grid container spacing={2}>
            {user.lastLogin && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Last Login:</strong> {formatDateHourMinuteSecond(user.lastLogin) || 'N/A'}
              </Grid>
            )}
            {user.loginFailCount !== undefined && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Login Failures:</strong> {user.loginFailCount}
              </Grid>
            )}
            {user.refreshTokenIssueDate && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Token Issue Date:</strong> {formatDateHourMinuteSecond(user.refreshTokenIssueDate) || 'N/A'}
              </Grid>
            )}
          </Grid>
        </Card>

        {/* Account Dates */}
        <Card sx={{ p: 2, marginBottom: 3 }}>
          <Typography variant="h6" gutterBottom>Account Dates</Typography>
          <Grid container spacing={2}>
            {user.createdAt && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Created:</strong> {formatDateHourMinuteSecond(user.createdAt) || 'N/A'}
              </Grid>
            )}
            {user.updatedAt && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Last Updated:</strong> {formatDateHourMinuteSecond(user.updatedAt) || 'N/A'}
              </Grid>
            )}
            {user.phoneConfirmedDate && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Phone Confirmed:</strong> {formatDateHourMinuteSecond(user.phoneConfirmedDate) || 'N/A'}
              </Grid>
            )}
          </Grid>
        </Card>
      </Box>
    </>
  );
};

export default UserPage;
